/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box, Button, Center, HStack, SimpleGrid, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {cssColorGroups} from '@salesforce/retail-react-app/app/constants';
import {
  ADD_FILTER_HIT_COUNT, REMOVE_FILTER_HIT_COUNT,
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils';

const ColorRefinements = ({filter, toggleFilter, selectedFilters}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('SwatchGroup', {
    variant: 'circle',
    disabled: false,
  });

  return (
    <SimpleGrid columns={2} mt={1} spacing={2}>
      {filter.values.map((value, idx) => {
        const isSelected = selectedFilters.includes(value.value);

        // Don't display refinements with no results, unless we got there by selecting too
        // many refinements
        if (value.hitCount === 0 && !isSelected) return; // eslint-disable-line array-callback-return

        return (
          <Box key={idx}>
            <HStack
              cursor="pointer"
              spacing={1}
              onClick={() => toggleFilter(value, filter.attributeId, isSelected)}
            >
              <Button
                {...styles.swatch}
                aria-checked={isSelected}
                border={isSelected ? '1px' : '0'}
                color={isSelected ? 'black' : 'gray.200'}
                marginBottom="-1px"
                marginRight={0}
                role="checkbox"
                variant="outline"
                aria-label={intl.formatMessage(
                  isSelected ? REMOVE_FILTER_HIT_COUNT : ADD_FILTER_HIT_COUNT,
                  value
                )}
              >
                <Center
                  {...styles.swatchButton}
                  border="1px solid black"
                  marginRight={0}
                >
                  <Box
                    backgroundRepeat="no-repeat"
                    backgroundSize="cover"
                    height="100%"
                    marginRight={0}
                    minWidth="32px"
                    width="100%"
                    background={
                      value.presentationId.toLowerCase() ===
                      'miscellaneous' &&
                      cssColorGroups[value.presentationId.toLowerCase()]
                    }
                    backgroundColor={
                      cssColorGroups[value.presentationId.toLowerCase()]
                    }
                  />
                </Center>
              </Button>
              <Text
                alignItems="center"
                aria-hidden="true" // avoid redundant readout since swatch has aria label
                display="flex"
                fontSize="sm"
                marginBottom="1px"
              >
                {intl.formatMessage(
                  {
                    id: 'colorRefinements.label.hitCount',
                    defaultMessage: '{colorLabel} ({colorHitCount})',
                  },
                  {colorLabel: value.label, colorHitCount: value.hitCount}
                )}
              </Text>
            </HStack>
          </Box>
        );
      })}
    </SimpleGrid>
  );
};

ColorRefinements.propTypes = {
  filter: PropTypes.object,
  toggleFilter: PropTypes.func,
  selectedFilters: PropTypes.array,
};

export default ColorRefinements;
